/**
 * CertificateOfCompletionReview · iter303ak-fix35 — Paid · T20 · Action 10
 * ────────────────────────────────────────────────────────────────────────
 * PER-CHILD certificate review. Now lives in the Paid workflow (moved out
 * of Completed) so it fires only after payment has been confirmed.
 *
 * Renders one panel per accepted child, each with:
 *   • Child header (Quote Type · variant · A#)
 *   • Editable form: quote-type `completion_description` + `completion_details`
 *     (auto-loaded from Settings → Quote Types; overridable per-child)
 *   • Read-only: per-child completion date (from Work In Progress sign-off)
 *     + per-child QI sign-off date (from Quality Inspection)
 *   • Stacked iframe preview (Qb=a) — one PDF per child
 *
 * Persists per-child overrides to:
 *   appraisal.completion_certificate.children[child_id] = {
 *     completion_date, qi_signoff_date, completion_description, completion_details
 *   }
 * via PATCH /appraisals/{id}/completion-certificate.
 */
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import axios from 'axios';
import { CheckCircle2, FileText, AlertTriangle, Save, ExternalLink, Info } from 'lucide-react';

const api = axios.create({ baseURL: `${process.env.REACT_APP_BACKEND_URL}/api` });
api.interceptors.request.use((cfg) => {
  if (t) cfg.headers.Authorization = `Bearer ${t}`;
  return cfg;
});

const BACKEND = process.env.REACT_APP_BACKEND_URL || '';
const childPdfUrl = (parent_id, child_id, cacheBust) => {
  const qs = new URLSearchParams({ inline: '1', child_id, ...(cacheBust ? { _v: String(cacheBust) } : {}) });
  const base = `${BACKEND}/api/reports/certificate-of-completion/${parent_id}?${qs.toString()}`;
  return base;
};

export default function CertificateOfCompletionReview({ action, capturedData, onNext, onBack, onCancel, canBack, value, onChange }) {
  const params = action.params || {};
  const parentId = capturedData?.appraisal_id;

  const parent = useMemo(() => (
    capturedData?.appraisal_record || capturedData?.appraisal || {}
  ), [capturedData]);

  const acceptedChildren = useMemo(() => {
    const decisions = parent.child_decisions || {};
    return Object.entries(decisions)
      .filter(([, d]) => d?.decision === 'accepted')
      .map(([child_id, d]) => ({ child_id, ...d }));
  }, [parent]);

  const wipSignoff = useMemo(
    () => (parent.work_execution?.per_child_signoff || {}),
    [parent],
  );
  const qiSignoff = useMemo(
    () => (parent.quality_inspection?.per_child_signoff || {}),
    [parent],
  );
  const priorOverrides = useMemo(
    () => (parent.completion_certificate?.children || {}),
    [parent],
  );

  // Per-child form state — {child_id: {completion_date, qi_signoff_date, completion_description, completion_details}}
  const [forms, setForms] = useState({});
  const [quoteTypeDefaults, setQuoteTypeDefaults] = useState({});   // {ref: {completion_description, completion_details}}
  const [cacheBust, setCacheBust] = useState(() => Date.now());
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveErr, setSaveErr] = useState('');
  const [loading, setLoading] = useState(true);

  // Hydrate forms from overrides + WIP/QI sign-offs + quote type defaults.
  const loadQuoteTypeDefaults = useCallback(async () => {
    const refs = Array.from(new Set(acceptedChildren.map(c => c.quote_type_ref).filter(Boolean)));
    if (!refs.length) { setLoading(false); return; }
    const map = {};
    for (const ref of refs) {
      try {
        const { data } = await api.get(`/quote-types/${encodeURIComponent(ref)}`);
        map[ref] = {
          completion_description: data?.completion_description || '',
          completion_details:     data?.completion_details || '',
        };
      } catch {
        map[ref] = { completion_description: '', completion_details: '' };
      }
    }
    setQuoteTypeDefaults(map);
    setLoading(false);
  }, [acceptedChildren]);
  useEffect(() => { loadQuoteTypeDefaults(); }, [loadQuoteTypeDefaults]);

  // Seed the forms once we have defaults + overrides.
  useEffect(() => {
    if (loading) return;
    setForms(prev => {
      const next = { ...prev };
      for (const c of acceptedChildren) {
        if (next[c.child_id]) continue;
        const ov = priorOverrides[c.child_id] || {};
        const qt = quoteTypeDefaults[c.quote_type_ref] || {};
        next[c.child_id] = {
          completion_date:         ov.completion_date || (wipSignoff[c.child_id]?.completion_date) || '',
          qi_signoff_date:         ov.qi_signoff_date || (qiSignoff[c.child_id]?.qi_signoff_date) || '',
          completion_description:  ov.completion_description ?? qt.completion_description ?? '',
          completion_details:      ov.completion_details ?? qt.completion_details ?? '',
        };
      }
      return next;
    });
  }, [loading, acceptedChildren, quoteTypeDefaults, priorOverrides, wipSignoff, qiSignoff]);

  useEffect(() => { if (onChange) onChange({ forms }); }, [forms]);  // eslint-disable-line react-hooks/exhaustive-deps

  const patchChild = (child_id, patch) => {
    setForms(prev => ({ ...prev, [child_id]: { ...(prev[child_id] || {}), ...patch } }));
    setDirty(true);
  };

  const resetToQuoteTypeDefaults = (child_id, quote_type_ref) => {
    const qt = quoteTypeDefaults[quote_type_ref] || {};
    patchChild(child_id, {
      completion_description: qt.completion_description || '',
      completion_details:     qt.completion_details || '',
    });
  };

  const save = useCallback(async () => {
    if (!parentId) return;
    setSaving(true);
    setSaveErr('');
    try {
      await api.patch(`/appraisals/${parentId}/completion-certificate`, {
        children: forms,
      });
      setCacheBust(Date.now());
      setDirty(false);
    } catch (e) {
      setSaveErr(e?.response?.data?.detail || e?.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  }, [parentId, forms]);

  const openChildPdf = (child_id) => {
    window.open(childPdfUrl(parentId, child_id, cacheBust), '_blank', 'noopener,noreferrer');
  };

  const readyToContinue = !dirty && Object.keys(forms).length > 0 && acceptedChildren.every(c => {
    const f = forms[c.child_id] || {};
    return f.completion_date && f.completion_description;
  });

  return (
    <div className="space-y-4" data-testid="wf-coc-review">
      <div className="rounded-xl border border-indigo-200 bg-indigo-50/60 p-4">
        <div className="flex items-start gap-3">
          <FileText className="w-5 h-5 mt-0.5 shrink-0 text-indigo-700" />
          <div className="flex-1">
            <div className="text-sm font-semibold text-indigo-900">Certificate of Completion · Per-Child Review</div>
            <div className="text-sm mt-1 text-indigo-800">
              {params.prompt || 'Preview + edit one certificate per accepted child. Completion date + QI sign-off date auto-load from Work In Progress and Quality Inspection. Description text auto-loads from Settings → Quote Types (edit here to override just for this cert).'}
            </div>
          </div>
        </div>
      </div>

      {loading && <div className="p-4 text-sm text-slate-500 italic">Loading quote-type defaults…</div>}

      {!loading && acceptedChildren.length === 0 && (
        <div className="p-4 border border-amber-200 bg-amber-50 rounded text-sm text-amber-800">
          No accepted children on this appraisal — nothing to certify.
        </div>
      )}

      {!loading && acceptedChildren.map(c => {
        const f = forms[c.child_id] || {};
        const qt = quoteTypeDefaults[c.quote_type_ref] || {};
        const hasQtDefaults = !!(qt.completion_description || qt.completion_details);
        const wipDate = wipSignoff[c.child_id]?.completion_date || '';
        const qiDate  = qiSignoff[c.child_id]?.qi_signoff_date || '';
        const usingQtOverride = (f.completion_description || '') !== (qt.completion_description || '')
          || (f.completion_details || '') !== (qt.completion_details || '');
        return (
          <div key={c.child_id} className="border border-slate-200 rounded-xl overflow-hidden bg-white"
            data-testid={`wf-coc-child-${c.child_id}`}>
            <div className="px-4 py-2 bg-slate-50 border-b border-slate-200 flex items-center gap-2 flex-wrap">
              <FileText size={14} className="text-slate-600" />
              <span className="text-sm font-semibold text-slate-800">
                {c.quote_type_ref}{c.variant_label ? ` · ${c.variant_label}` : ''}
              </span>
              {c.appraisal_no && (
                <span className="text-[10px] font-bold uppercase tracking-widest text-indigo-800 bg-indigo-100 border border-indigo-300 rounded px-1.5 py-0.5">
                  A{c.appraisal_no}
                </span>
              )}
              {!hasQtDefaults && (
                <span className="text-[10px] font-semibold text-amber-700 bg-amber-100 border border-amber-300 rounded px-1.5 py-0.5 inline-flex items-center gap-1">
                  <Info size={9} /> Quote type has no completion description — set in Settings → Quote Types
                </span>
              )}
              <button onClick={() => openChildPdf(c.child_id)}
                data-testid={`wf-coc-open-${c.child_id}`}
                className="ml-auto px-2.5 py-1 rounded text-xs font-semibold text-slate-700 bg-white border border-slate-300 hover:bg-slate-50 inline-flex items-center gap-1">
                <ExternalLink size={12} /> Open PDF
              </button>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-0">
              {/* Editable form */}
              <div className="p-4 space-y-3 border-b lg:border-b-0 lg:border-r border-slate-200">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                  <div>
                    <label className="block text-[10px] font-semibold uppercase tracking-widest text-slate-500 mb-0.5">
                      Completion date <span className="text-slate-400 font-normal normal-case">(from WIP)</span>
                    </label>
                    <input type="text" value={f.completion_date || ''}
                      onChange={(e) => patchChild(c.child_id, { completion_date: e.target.value })}
                      placeholder={wipDate || 'YYYY-MM-DD'}
                      data-testid={`wf-coc-completion-date-${c.child_id}`}
                      className="w-full px-2 py-1 text-xs border border-slate-300 rounded font-mono" />
                    {wipDate && f.completion_date !== wipDate && (
                      <button onClick={() => patchChild(c.child_id, { completion_date: wipDate })}
                        data-testid={`wf-coc-completion-date-reset-${c.child_id}`}
                        className="text-[10px] text-indigo-700 underline hover:text-indigo-900 mt-0.5">
                        Reset to WIP date ({wipDate})
                      </button>
                    )}
                  </div>
                  <div>
                    <label className="block text-[10px] font-semibold uppercase tracking-widest text-slate-500 mb-0.5">
                      QI sign-off date <span className="text-slate-400 font-normal normal-case">(from QI)</span>
                    </label>
                    <input type="text" value={f.qi_signoff_date || ''}
                      onChange={(e) => patchChild(c.child_id, { qi_signoff_date: e.target.value })}
                      placeholder={qiDate || 'YYYY-MM-DD'}
                      data-testid={`wf-coc-qi-date-${c.child_id}`}
                      className="w-full px-2 py-1 text-xs border border-slate-300 rounded font-mono" />
                    {qiDate && f.qi_signoff_date !== qiDate && (
                      <button onClick={() => patchChild(c.child_id, { qi_signoff_date: qiDate })}
                        data-testid={`wf-coc-qi-date-reset-${c.child_id}`}
                        className="text-[10px] text-indigo-700 underline hover:text-indigo-900 mt-0.5">
                        Reset to QI date ({qiDate})
                      </button>
                    )}
                  </div>
                </div>
                <div>
                  <div className="flex items-center gap-2 mb-0.5">
                    <label className="text-[10px] font-semibold uppercase tracking-widest text-slate-500">
                      Completion description <span className="text-slate-400 font-normal normal-case">(Quote-type default)</span>
                    </label>
                    {usingQtOverride && hasQtDefaults && (
                      <button onClick={() => resetToQuoteTypeDefaults(c.child_id, c.quote_type_ref)}
                        data-testid={`wf-coc-desc-reset-${c.child_id}`}
                        className="ml-auto text-[10px] text-indigo-700 underline hover:text-indigo-900">
                        Reset to Quote Type default
                      </button>
                    )}
                  </div>
                  <textarea value={f.completion_description || ''}
                    onChange={(e) => patchChild(c.child_id, { completion_description: e.target.value })}
                    placeholder="Auto-loads from Settings → Quote Types. Override just for this cert if you need to."
                    data-testid={`wf-coc-description-${c.child_id}`}
                    rows={3}
                    className="w-full px-2 py-1 text-xs border border-slate-300 rounded" />
                </div>
                <div>
                  <label className="block text-[10px] font-semibold uppercase tracking-widest text-slate-500 mb-0.5">
                    Completion details <span className="text-slate-400 font-normal normal-case">(bullet list · one per line)</span>
                  </label>
                  <textarea value={f.completion_details || ''}
                    onChange={(e) => patchChild(c.child_id, { completion_details: e.target.value })}
                    placeholder={'• Physical inspection complete\n• Areas graded and photo-documented\n• Warranty period commenced'}
                    data-testid={`wf-coc-details-${c.child_id}`}
                    rows={4}
                    className="w-full px-2 py-1 text-xs border border-slate-300 rounded font-mono" />
                </div>
              </div>
              {/* Preview */}
              <div className="bg-slate-50">
                <div className="px-3 py-1.5 bg-white border-b border-slate-200 text-[10px] font-semibold uppercase tracking-widest text-slate-500">
                  Live preview (this child)
                </div>
                <iframe src={childPdfUrl(parentId, c.child_id, cacheBust)}
                  title={`Certificate of Completion preview — ${c.child_id}`}
                  data-testid={`wf-coc-iframe-${c.child_id}`}
                  className="w-full border-0 bg-white"
                  style={{ height: 520 }} />
              </div>
            </div>
          </div>
        );
      })}

      {saveErr && (
        <div className="flex items-start gap-2 text-sm text-rose-700 bg-rose-50 border border-rose-200 rounded p-3">
          <AlertTriangle className="w-4 h-4 mt-0.5" /> <span>{saveErr}</span>
        </div>
      )}

      <div className="flex items-center justify-between gap-3 pt-2">
        {canBack ? <button onClick={onBack} className="px-3 py-1.5 rounded-md text-sm text-slate-600 hover:bg-slate-100">← Back</button> : <span />}
        <div className="flex gap-2 flex-wrap">
          {onCancel && <button onClick={onCancel} className="px-3 py-1.5 rounded-md text-sm text-slate-600 hover:bg-slate-100">Cancel</button>}
          <button onClick={save} disabled={saving || !dirty}
            data-testid="wf-coc-save"
            className="px-4 py-1.5 rounded-md text-sm font-semibold text-white bg-sky-600 hover:bg-sky-700 disabled:opacity-40 inline-flex items-center gap-2">
            <Save size={14} /> {saving ? 'Saving…' : (dirty ? 'Save & refresh all previews' : 'Saved')}
          </button>
          <button onClick={onNext} disabled={!readyToContinue}
            data-testid="wf-coc-continue"
            className="px-4 py-1.5 rounded-md text-sm bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-40 inline-flex items-center gap-2">
            <CheckCircle2 className="w-4 h-4" /> {dirty ? 'Save first' : 'Continue'}
          </button>
        </div>
      </div>
    </div>
  );
}
